import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Cart = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, cartTotal, cartItemCount } = useCart();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  if (cart.length === 0) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 text-center">
        <ShoppingBag size={64} className="mx-auto text-gray-300 mb-6" />
        <h2 className="text-2xl font-bold mb-4" data-testid="empty-cart-title">Your cart is empty</h2>
        <p className="text-gray-600 mb-8">Add some products to get started!</p>
        <Button onClick={() => navigate('/products')} className="bg-[#D4AF37] text-white hover:bg-[#c9a22e]" data-testid="continue-shopping-btn">
          Continue Shopping
        </Button>
      </div>
    );
  }

  const subtotal = cartTotal;
  const discount = settings ? (subtotal * settings.discount) / 100 : 0;
  const afterDiscount = Math.max(0, subtotal - discount);
  const gst = settings ? (afterDiscount * settings.gst) / 100 : 0;
  const shipping = settings ? (subtotal >= settings.freeShippingThreshold ? 0 : settings.shipping) : 0;
  const extraCharges = settings?.extraCharges || 0;
  const extraChargesLabel = settings?.extraChargesLabel || 'Other Charges';
  const total = afterDiscount + gst + shipping + extraCharges;

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
      <h1 className="text-3xl md:text-4xl font-bold mb-8" style={{fontFamily: 'Playfair Display, serif'}} data-testid="cart-title">
        Shopping Cart
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {cart.map((item) => (
            <div key={item.id} className="card p-6 flex gap-6" data-testid={`cart-item-${item.id}`}>
              <img src={item.image} alt={item.name} className="w-24 h-24 object-cover cursor-pointer" onClick={() => navigate(`/products/${item.id}`)} />
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-2 cursor-pointer hover:text-[#D4AF37]" onClick={() => navigate(`/products/${item.id}`)}>{item.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{item.category}</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 border border-gray-300">
                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="p-2 hover:bg-gray-50" data-testid={`decrease-qty-${item.id}`}><Minus size={16} /></button>
                    <span className="px-4" data-testid={`item-quantity-${item.id}`}>{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="p-2 hover:bg-gray-50" data-testid={`increase-qty-${item.id}`}><Plus size={16} /></button>
                  </div>
                  <button onClick={() => { removeFromCart(item.id); toast.success('Item removed'); }} className="text-red-500 hover:text-red-700" data-testid={`remove-item-${item.id}`}><Trash2 size={18} /></button>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[#D4AF37] font-bold text-xl" data-testid={`item-total-${item.id}`}>₹{(item.price * item.quantity).toLocaleString()}</p>
                <p className="text-gray-500 text-sm">₹{item.price} each</p>
              </div>
            </div>
          ))}
        </div>

        <div>
          <div className="card p-6 sticky top-24">
            <h2 className="text-xl font-bold mb-6">Order Summary</h2>
            <div className="space-y-2.5 mb-5 pb-5 border-b border-gray-200 text-sm">
              <div className="flex justify-between text-gray-600"><span>Subtotal ({cartItemCount} items)</span><span data-testid="cart-subtotal">₹{subtotal.toLocaleString()}</span></div>
              {discount > 0 && <div className="flex justify-between text-green-600"><span>Discount ({settings.discount}%)</span><span>-₹{discount.toFixed(2)}</span></div>}
              <div className="flex justify-between text-gray-600"><span>GST ({settings?.gst || 18}%)</span><span data-testid="cart-gst">₹{gst.toFixed(2)}</span></div>
              <div className="flex justify-between text-gray-600"><span>Shipping</span><span data-testid="cart-shipping">{shipping === 0 ? <span className="text-green-600">FREE</span> : `₹${shipping.toFixed(2)}`}</span></div>
              {extraCharges > 0 && <div className="flex justify-between text-gray-600"><span>{extraChargesLabel}</span><span>₹{extraCharges.toFixed(2)}</span></div>}
            </div>

            <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2f4a] rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-white/70 font-medium">Estimated Total</span>
                <span className="text-2xl font-bold text-[#D4AF37]" data-testid="cart-total">₹{total.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
              </div>
              {shipping === 0 && <p className="text-[10px] text-green-400 mt-1">Free shipping applied!</p>}
            </div>

            <Button onClick={() => navigate('/checkout')} className="w-full bg-[#D4AF37] text-white hover:bg-[#c9a22e] h-12 text-base font-bold" data-testid="proceed-checkout-btn">
              Proceed to Checkout
            </Button>
            <Button onClick={() => navigate('/products')} variant="outline" className="w-full mt-3 border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white" data-testid="continue-shopping-link">
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
